import MobileSheet from '../components/MobileSheet';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi, authApi } from '../lib/api';
import type { ScriptSummary, TemplateKey } from '../../shared/types';
import { STATUS_LABELS } from '../../shared/types';
import Wordmark from '../components/Wordmark';
import Eyebrow from '../components/Eyebrow';
import ThemeToggle from '../components/ThemeToggle';
import Button from '../components/Button';
import RowMenu from '../components/RowMenu';
import TranscriptDraftModal from '../components/TranscriptDraftModal';
import CutdownDialog from '../components/Cutdown/CutdownDialog';
import { createdToast } from '../components/Cutdown/cutdownCopy';
import Toaster, { showToast } from '../components/Toaster';
import BundlesView from '../components/BundlesView';
import { timeAgo } from '../lib/timeAgo';
import { useScriptSearch } from '../hooks/useScriptSearch';
import SearchSnippet from '../components/SearchSnippet';
import type { SearchResult } from '../../shared/search';
import {
  DASHBOARD_SLUG_HINT,
  DASHBOARD_SLUG_HTML_PATTERN,
  normaliseDashboardSlugInput,
} from '../../shared/dashboardSlug';

const TEMPLATES: Array<{ key: TemplateKey; label: string }> = [
  { key: 'explainer', label: 'Explainer / product video' },
  { key: 'brand_film', label: 'Brand film / hero video' },
  { key: 'testimonial', label: 'Testimonial / case study' },
  { key: 'social_short', label: 'Social / short-form ad' },
  { key: 'fb_ad_campaign', label: 'Facebook Ad Campaign' },
  { key: 'interview_brief', label: 'Interview brief' },
];

type ScriptActionTarget = Pick<ScriptSummary, 'id' | 'title' | 'client_name'>;

type View = 'all' | 'clients' | 'links' | 'archived' | 'trashed';
const VIEW_KEY = 'hsp_dashboard_view';

function readView(): View {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (v === 'all' || v === 'clients' || v === 'links' || v === 'archived' || v === 'trashed') {
      return v;
    }
  } catch {}
  return 'all';
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState<ScriptSummary[] | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<ScriptActionTarget | null>(null);
  const [cutdownTarget, setCutdownTarget] = useState<ScriptActionTarget | null>(null);
  const [view, setView] = useState<View>(() => readView());
  const [search, setSearch] = useState('');

  const [revision, setRevision] = useState(0);
  const query = search.trim();
  const searchView = view === 'archived' ? 'archived' : view === 'all' || view === 'clients' ? 'active' : null;
  const searching = !!query && searchView !== null;
  const searchState = useScriptSearch(query, searchView, revision);

  function refresh() {
    setScripts(null);
    setRevision((value) => value + 1);
  }

  useEffect(() => {
    // Bundles keep their own list and filtering. Search results do not depend
    // on the capped dashboard list finishing (or containing the matched script).
    if (view === 'links') return;
    let cancelled = false;
    setScripts(null);
    const apiView = view === 'archived' ? 'archived' : view === 'trashed' ? 'trashed' : 'active';
    adminApi.listScripts({ view: apiView }).then((res) => {
      if (!cancelled) setScripts(res.scripts);
    });
    return () => { cancelled = true; };
  }, [view, revision]);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {}
  }, [view]);

  // Trash retains its metadata-only filter; active/archive search uses the
  // server index so results can include scripts beyond the loaded list.
  const filteredScripts = useMemo(() => {
    if (!scripts) return null;
    const q = search.trim().toLowerCase();
    if (!q || view !== 'trashed') return scripts;
    return scripts.filter((s) => {
      return (
        s.title.toLowerCase().includes(q) ||
        s.client_name.toLowerCase().includes(q) ||
        s.project_name.toLowerCase().includes(q)
      );
    });
  }, [scripts, search, view]);

  async function handleLogout() {
    await authApi.logout();
    navigate('/admin/login', { replace: true });
  }

  async function confirmDelete() {
    if (!deleteCandidate) return;
    // From the Trash tab the candidate is a permanent delete; from any
    // other view it's a soft delete (moves to Trash).
    const permanent = view === 'trashed';
    await adminApi.deleteScript(deleteCandidate.id, { permanent });
    setDeleteCandidate(null);
    refresh();
  }

  async function handleDuplicate(script: ScriptActionTarget) {
    const result = await adminApi.duplicateScript(script.id);
    navigate(`/admin/scripts/${result.id}`);
  }

  async function handleArchive(script: ScriptActionTarget, archived: boolean) {
    await adminApi.setArchived(script.id, archived);
    refresh();
  }

  async function handleRestore(script: ScriptActionTarget) {
    await adminApi.restoreScript(script.id);
    refresh();
  }

  return (
    <main data-context="admin" className="mobile-dashboard min-h-screen bg-page text-fg">
      <header className="border-b border-line bg-page/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 md:px-12 min-h-16 md:h-16 py-2 md:py-0 flex flex-wrap md:flex-nowrap items-center justify-between gap-x-3 gap-y-1 md:gap-0">
          <Link to="/admin" className="flex items-center gap-3 shrink-0 whitespace-nowrap md:shrink md:whitespace-normal">
            <Wordmark size="sm" />
            <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.12em] text-faint border-l border-line pl-3 ml-1">
              Scripts · Admin
            </span>
          </Link>
          <div className="flex items-center gap-1 md:gap-2 shrink-0 md:shrink">
            <ThemeToggle />
            <Link to="/admin/security">
              <Button variant="ghost" size="sm" className="whitespace-nowrap md:whitespace-normal">
                Security
              </Button>
            </Link>
            <Button variant="ghost" size="sm" className="whitespace-nowrap md:whitespace-normal" onClick={handleLogout}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="flex flex-col gap-5 md:gap-0 md:flex-row md:items-end md:justify-between mb-8 animate-rise-subtle">
          <div>
            <Eyebrow>
              //{' '}
              {view === 'all'
                ? 'All scripts'
                : view === 'clients'
                  ? 'Clients'
                  : view === 'links'
                    ? 'Bundles'
                    : view === 'archived'
                      ? 'Archived'
                      : 'Trash'}
            </Eyebrow>
            <h1 className="font-display font-bold text-[40px] md:text-[56px] leading-[1.0] tracking-[-0.02em] mt-3">
              {view === 'all'
                ? 'Your scripts'
                : view === 'clients'
                  ? 'Your clients'
                  : view === 'links'
                    ? 'Links'
                    : view === 'archived'
                      ? 'Archived scripts'
                      : 'Trash'}
            </h1>
          </div>
          <div className="flex flex-wrap md:flex-nowrap items-center gap-2">
            {view === 'links' ? (
              <Button variant="primary" glow className="whitespace-nowrap md:whitespace-normal" onClick={() => navigate('/admin/links/new')}>
                <span className="text-base leading-none">+</span>
                New link
              </Button>
            ) : (
              <>
                <Button variant="secondary" className="whitespace-nowrap md:whitespace-normal" onClick={() => setShowDraft(true)}>
                  <span className="text-base leading-none">⚡</span>
                  Draft from transcript
                </Button>
                <Button variant="primary" glow className="whitespace-nowrap md:whitespace-normal" onClick={() => setShowNew(true)}>
                  <span className="text-base leading-none">+</span>
                  New script
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <ViewTabs view={view} onChange={(nextView) => { if (nextView !== view) { setScripts(null); setView(nextView); } }} />
          <SearchInput
            value={search}
            maxLength={searchView ? 200 : undefined}
            onChange={setSearch}
            placeholder={view === 'links' ? 'Search bundles, clients…' : view === 'trashed' ? 'Search titles, clients, projects…' : 'Search scripts, clients and content…'}
          />
        </div>

        {view === 'links' ? (
          <BundlesView search={search} />
        ) : searching ? (
          <section aria-label="Search results" aria-busy={searchState.status === 'loading'}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 text-[12px] text-muted mb-4" role="status" aria-live="polite">
              <strong className="font-medium text-fg break-words min-w-0">
                {searchState.status === 'loading' ? 'Searching…' : searchState.status === 'error' ? 'Search unavailable' :
                  `${searchState.has_more ? 'First ' : ''}${searchState.results.length} result${searchState.results.length === 1 ? '' : 's'} for “${query}”`}
              </strong>
              <span>{view === 'archived' ? 'Archived' : 'Active'} scripts · Most relevant first</span>
            </div>
            {searchState.status === 'loading' ? (
              <SkeletonList searchResults />
            ) : searchState.status === 'error' ? (
              <div className="border border-dashed border-line rounded-md px-6 py-16 text-center bg-card">
                <Eyebrow>// Search unavailable</Eyebrow>
                <h2 className="font-display font-bold text-[28px] mt-3 mb-3">We couldn’t search your scripts</h2>
                <p className="text-muted mb-6">Your search is still here. Try again in a moment.</p>
                <Button variant="primary" onClick={() => setRevision((value) => value + 1)}>Try again</Button>
              </div>
            ) : searchState.results.length === 0 ? (
              <SearchEmptyState query={query} onClear={() => setSearch('')} contentSearch />
            ) : (
              <>
                <ScriptList
                  scripts={searchState.results}
                  onDuplicate={handleDuplicate}
                  onCutdown={setCutdownTarget}
                  onDelete={setDeleteCandidate}
                  onArchive={handleArchive}
                  archivedView={view === 'archived'}
                />
                {searchState.has_more && <p className="text-muted text-[13px] mt-4">More matches are available. Refine your search to narrow the results.</p>}
              </>
            )}
            <p className="mt-6 text-faint text-[12px] text-center">Search includes titles, clients, projects, script text, scratchpads and brief fields.</p>
          </section>
        ) : scripts === null ? (
          <SkeletonList />
        ) : scripts.length === 0 ? (
          view === 'archived' ? (
            <ArchivedEmptyState />
          ) : view === 'trashed' ? (
            <TrashEmptyState />
          ) : (
            <EmptyState onCreate={() => setShowNew(true)} />
          )
        ) : filteredScripts && filteredScripts.length === 0 ? (
          <SearchEmptyState query={search} onClear={() => setSearch('')} />
        ) : view === 'clients' ? (
          <ClientsView
            scripts={filteredScripts ?? scripts}
            onDuplicate={handleDuplicate}
            onCutdown={setCutdownTarget}
            onDelete={setDeleteCandidate}
            onArchive={handleArchive}
          />
        ) : (
          <ScriptList
            scripts={filteredScripts ?? scripts}
            onDuplicate={handleDuplicate}
            onCutdown={setCutdownTarget}
            onDelete={setDeleteCandidate}
            onArchive={handleArchive}
            onRestore={handleRestore}
            archivedView={view === 'archived'}
            trashedView={view === 'trashed'}
          />
        )}
      </section>

      {showNew && <NewScriptModal onClose={() => setShowNew(false)} />}
      {showDraft && (
        <TranscriptDraftModal
          onClose={() => setShowDraft(false)}
          onCreated={(firstId) => {
            setShowDraft(false);
            navigate(`/admin/scripts/${firstId}`);
          }}
        />
      )}
      {deleteCandidate && (
        <DeleteConfirmModal
          script={deleteCandidate}
          permanent={view === 'trashed'}
          onCancel={() => setDeleteCandidate(null)}
          onConfirm={confirmDelete}
        />
      )}
      {cutdownTarget && (
        <CutdownDialog
          scriptId={cutdownTarget.id}
          title={cutdownTarget.title}
          onClose={() => setCutdownTarget(null)}
          onCreated={(result) => {
            setCutdownTarget(null);
            refresh();
            const toast = createdToast(result);
            const open = toast.open;
            showToast('success', toast.text, open ? {
              action: { label: open.label, onClick: () => navigate(`/admin/scripts/${open.id}`) },
            } : undefined);
          }}
        />
      )}
      <Toaster />
    </main>
  );
}

// ---------- Tab control ----------

function ViewTabs({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  return (
    <div className="inline-flex max-w-full overflow-x-auto md:max-w-none md:overflow-x-visible rounded-full bg-elevated border border-line p-0.5">
      {(
        [
          { id: 'all', label: 'All scripts' },
          { id: 'clients', label: 'Clients' },
          { id: 'links', label: 'Links' },
          { id: 'archived', label: 'Archived' },
          { id: 'trashed', label: 'Trash' },
        ] as Array<{ id: View; label: string }>
      ).map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`shrink-0 whitespace-nowrap md:shrink md:whitespace-normal px-3.5 py-1.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] font-medium transition-colors duration-200 ${
            view === tab.id ? 'bg-fg text-page' : 'text-muted hover:text-fg'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function SearchInput({
  value,
  onChange,
  placeholder = 'Search scripts, clients…',
  maxLength,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <div className="relative w-full md:max-w-sm">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-faint pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <circle cx="10.8" cy="10.8" r="6.8" /><path d="m16 16 5 5" />
      </svg>
      <input
        type="search"
        aria-label={placeholder.replace(/…$/, '')}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="
          w-full pl-9 pr-8 py-1.5 rounded-full
          bg-elevated border border-line text-fg text-[13px] placeholder:text-faint
          focus:border-accent focus:shadow-focus focus:outline-none
          transition-all duration-150
          [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden
        "
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-faint hover:text-fg text-[14px] leading-none w-5 h-5 flex items-center justify-center"
        >
          ×
        </button>
      )}
    </div>
  );
}

function SearchEmptyState({ query, onClear, contentSearch = false }: { query: string; onClear: () => void; contentSearch?: boolean }) {
  return (
    <div className="border border-dashed border-line rounded-md px-12 py-16 text-center bg-card">
      <Eyebrow>// No matches</Eyebrow>
      <h2 className="font-display font-bold text-[28px] leading-[1.1] tracking-[-0.02em] mt-3 mb-3 break-words">
        Nothing matches "{query}"
      </h2>
      <p className="text-muted max-w-md mx-auto mb-6">
        {contentSearch ? 'We searched titles, clients, projects and content.' : 'We searched titles, clients and project names.'} Try another word, or clear the search.
      </p>
      <Button variant="ghost" onClick={onClear}>
        Clear search
      </Button>
    </div>
  );
}

// ---------- Script list (used in All scripts view + within client groups) ----------

interface ScriptListProps {
  scripts: Array<ScriptSummary | SearchResult>;
  onDuplicate: (s: ScriptActionTarget) => void;
  /** Scripts only: briefs have no Audio column to cut. */
  onCutdown?: (s: ScriptActionTarget) => void;
  onDelete: (s: ScriptActionTarget) => void;
  onArchive: (s: ScriptActionTarget, archived: boolean) => void;
  onRestore?: (s: ScriptActionTarget) => void;
  staggerStart?: number;
  showClient?: boolean;
  archivedView?: boolean;
  trashedView?: boolean;
}

function ScriptList({
  scripts,
  onDuplicate,
  onCutdown,
  onDelete,
  onArchive,
  onRestore,
  staggerStart = 0,
  showClient = true,
  archivedView = false,
  trashedView = false,
}: ScriptListProps) {
  const [viewScript, setViewScript] = useState<ScriptActionTarget | null>(null);
  const navigate = useNavigate();
  return (
    <>
    <ul className="space-y-2">
      {scripts.map((s, i) => (
        <li
          key={s.id}
          className="relative group animate-rise-subtle"
          style={{ animationDelay: `${Math.min(staggerStart + i, 6) * 60}ms` }}
        >
          <Link
            to={`/admin/scripts/${s.id}`}
            className="
              block bg-card border border-line rounded-md
              px-6 py-5 pr-12
              transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
              group-hover:border-accent group-hover:-translate-y-px
            "
          >
            <div className="flex flex-wrap md:flex-nowrap items-center md:justify-between gap-x-3 gap-y-2 md:gap-6">
              <div className="min-w-0 basis-full md:basis-0 md:flex-1">
                <div className="font-display font-semibold text-[18px] text-fg truncate">
                  {s.title}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[13px] text-muted flex-wrap">
                  {showClient && (
                    <>
                      <span className="min-w-0 max-w-full break-words">{s.client_name}</span>
                      <Dot />
                    </>
                  )}
                  {'snippet' in s && s.project_name && <><span className="min-w-0 max-w-full break-words">{s.project_name}</span><Dot /></>}
                  {s.template_key === 'interview_brief' ? (
                    <span className="px-1.5 py-px rounded-full border border-line font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
                      Interview brief
                    </span>
                  ) : (
                    <span>{labelForTemplate(s.template_key)}</span>
                  )}
                  <Dot />
                  <span className="text-faint">Updated {timeAgo(s.updated_at)}</span>
                  {'last_viewed_at' in s && s.last_viewed_at && (
                    <>
                      <Dot />
                      <span className="text-accent">
                        Client opened {timeAgo(s.last_viewed_at)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {'last_heartbeat_at' in s && s.last_heartbeat_at && Date.now() - s.last_heartbeat_at < 60_000 && (
                <PresenceBadge />
              )}
              {'feedback_submitted_at' in s && s.feedback_submitted_at && (
                <FeedbackReadyBadge
                  authorName={s.feedback_submitted_by}
                  submittedAt={s.feedback_submitted_at}
                />
              )}
              {'pending_suggestion_count' in s && s.pending_suggestion_count > 0 && <SuggestionBadge />}
              {'open_comment_count' in s && s.open_comment_count > 0 && <CommentsBadge count={s.open_comment_count} />}
              <StatusPill status={s.status} />
            </div>
            {'snippet' in s && <SearchSnippet result={s} />}
          </Link>
          {!trashedView && <div className="mobile-list-actions md:hidden">
            <Link to={`/admin/scripts/${s.id}`}>Edit {s.template_key === 'interview_brief' ? 'brief' : 'script'}</Link>
            <button onClick={() => setViewScript(s)}>Views ▾<span className="sr-only"> for {s.title}</span></button>
          </div>}
          <div className={`absolute right-3 ${'snippet' in s ? 'top-5' : 'top-1/2 -translate-y-1/2'}`}>
            <RowMenu
              items={
                trashedView
                  ? [
                      ...(onRestore
                        ? [{ label: 'Restore', onClick: () => onRestore(s) }]
                        : []),
                      {
                        label: 'Delete permanently',
                        onClick: () => onDelete(s),
                        destructive: true,
                      },
                    ]
                  : [
                      { label: 'Duplicate', onClick: () => onDuplicate(s) },
                      ...(onCutdown && s.template_key !== 'interview_brief'
                        ? [{ label: 'Make a cutdown', onClick: () => onCutdown(s) }]
                        : []),
                      archivedView
                        ? { label: 'Unarchive', onClick: () => onArchive(s, false) }
                        : { label: 'Archive', onClick: () => onArchive(s, true) },
                      { label: 'Delete', onClick: () => onDelete(s), destructive: true },
                    ]
              }
            />
          </div>
        </li>
      ))}
    </ul>
    <MobileSheet open={!!viewScript} title="Choose your view" onClose={() => setViewScript(null)}>
      {(['editor', 'client', 'shoot'] as const).map(view => <button className="mobile-nav-row" key={view} onClick={() => { if (viewScript) navigate(`/admin/scripts/${viewScript.id}?osView=${view}`); }}>
        {view === 'editor' ? 'Editor' : view === 'client' ? 'Client review' : 'Shoot view'}<span aria-hidden>›</span>
      </button>)}
    </MobileSheet>
    </>
  );
}

// ---------- Clients view ----------

interface ClientGroup {
  name: string;
  scripts: ScriptSummary[];
  lastActivity: number;
}

function groupByClient(scripts: ScriptSummary[]): ClientGroup[] {
  const map = new Map<string, ClientGroup>();
  for (const s of scripts) {
    const existing = map.get(s.client_name);
    if (existing) {
      existing.scripts.push(s);
      existing.lastActivity = Math.max(existing.lastActivity, s.updated_at);
    } else {
      map.set(s.client_name, {
        name: s.client_name,
        scripts: [s],
        lastActivity: s.updated_at,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.lastActivity - a.lastActivity);
}

function ClientsView({
  scripts,
  onDuplicate,
  onCutdown,
  onDelete,
  onArchive,
}: {
  scripts: ScriptSummary[];
  onDuplicate: (s: ScriptActionTarget) => void;
  onCutdown: (s: ScriptActionTarget) => void;
  onDelete: (s: ScriptActionTarget) => void;
  onArchive: (s: ScriptActionTarget, archived: boolean) => void;
}) {
  const groups = useMemo(() => groupByClient(scripts), [scripts]);
  return (
    <div className="space-y-10">
      {groups.map((group, gi) => (
        <section
          key={group.name}
          className="animate-rise-subtle"
          style={{ animationDelay: `${Math.min(gi, 6) * 60}ms` }}
        >
          <header className="flex items-baseline justify-between gap-4 mb-4 pb-3 border-b border-line">
            <h2 className="font-display font-semibold text-[24px] tracking-[-0.01em] text-fg">
              {group.name}
            </h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint shrink-0">
              {group.scripts.length} script{group.scripts.length === 1 ? '' : 's'} · {timeAgo(group.lastActivity)}
            </span>
          </header>
          <ScriptList
            scripts={group.scripts}
            onDuplicate={onDuplicate}
            onCutdown={onCutdown}
            onDelete={onDelete}
            onArchive={onArchive}
            showClient={false}
          />
        </section>
      ))}
    </div>
  );
}

// ---------- Misc ----------

function Dot() {
  return <span className="text-faint/60" aria-hidden>·</span>;
}

function SkeletonList({ searchResults = false }: { searchResults?: boolean }) {
  return (
    <ul className="space-y-2">
      {[0, 1, 2].map((i) => (
        <li key={i} className={`${searchResults ? 'h-[149px]' : 'h-[80px]'} bg-card border border-line rounded-md skeleton`} />
      ))}
    </ul>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="border border-dashed border-line rounded-md px-12 py-16 md:py-24 text-center bg-card">
      <Eyebrow>// Empty</Eyebrow>
      <h2 className="font-display font-bold text-[32px] md:text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-3">
        No scripts yet
      </h2>
      <p className="text-muted max-w-md mx-auto mb-8">
        Create your first script and share a branded review link with your client.
      </p>
      <Button variant="primary" glow onClick={onCreate}>
        Create a script
      </Button>
    </div>
  );
}

function ArchivedEmptyState() {
  return (
    <div className="border border-dashed border-line rounded-md px-12 py-16 md:py-24 text-center bg-card">
      <Eyebrow>// Archive</Eyebrow>
      <h2 className="font-display font-bold text-[32px] md:text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-3">
        Nothing archived
      </h2>
      <p className="text-muted max-w-md mx-auto">
        Once a script is shipped, archive it from the row menu to tuck it away
        without losing its review URL or comments.
      </p>
    </div>
  );
}

function TrashEmptyState() {
  return (
    <div className="border border-dashed border-line rounded-md px-12 py-16 md:py-24 text-center bg-card">
      <Eyebrow>// Trash</Eyebrow>
      <h2 className="font-display font-bold text-[32px] md:text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-3">
        Trash is empty
      </h2>
      <p className="text-muted max-w-md mx-auto">
        Deleted scripts land here for 30 days before they're purged. You can
        restore from this view at any time.
      </p>
    </div>
  );
}

function StatusPill({ status }: { status: ScriptSummary['status'] }) {
  const map: Record<ScriptSummary['status'], string> = {
    draft: 'border-line text-muted',
    v1: 'border-accent/60 text-accent',
    v2: 'border-accent/60 text-accent',
    v3: 'border-accent/60 text-accent',
    approved: 'border-accent bg-accent/10 text-accent',
  };
  return (
    <span
      className={`shrink-0 font-mono text-[10px] uppercase font-medium tracking-[0.12em] px-2.5 py-1 rounded-full border ${map[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function FeedbackReadyBadge({
  authorName,
  submittedAt,
}: {
  authorName: string;
  submittedAt: number;
}) {
  const ago = timeAgo(submittedAt);
  const tooltip = authorName
    ? `${authorName} sent for review · ${ago}`
    : `Sent for review · ${ago}`;
  return (
    <span
      className="
        inline-flex items-center gap-1.5 shrink-0
        px-2 py-1 rounded-full
        bg-accent text-accent-fg
        font-mono text-[10px] uppercase tracking-[0.12em] font-medium
        shadow-flare/40
      "
      title={tooltip}
    >
      <span className="w-1 h-1 rounded-full bg-accent-fg" aria-hidden />
      Feedback ready
    </span>
  );
}

function SuggestionBadge() {
  return (
    <span
      className="
        inline-flex items-center gap-1.5 shrink-0
        px-2 py-1 rounded-full
        bg-accent text-accent-fg
        font-mono text-[10px] uppercase tracking-[0.12em] font-medium
        shadow-flare/40
      "
      title="Suggested edits awaiting review"
    >
      <span className="w-1 h-1 rounded-full bg-accent-fg" aria-hidden />
      Suggestion
    </span>
  );
}

function PresenceBadge() {
  return (
    <span
      className="
        inline-flex items-center gap-1.5 shrink-0
        px-2 py-1 rounded-full
        bg-accent/10 border border-accent/40 text-accent
        font-mono text-[10px] uppercase tracking-[0.12em] font-medium
      "
      title="A client is viewing this script right now."
    >
      <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" aria-hidden />
      Viewing now
    </span>
  );
}

function CommentsBadge({ count }: { count: number }) {
  return (
    <span
      className="
        inline-flex items-center gap-1.5 shrink-0
        px-2 py-1 rounded-full
        bg-accent/10 border border-accent/40 text-accent
        font-mono text-[10px] uppercase tracking-[0.12em] font-medium
      "
      title={`${count} open comment${count === 1 ? '' : 's'}`}
    >
      <span className="w-1 h-1 rounded-full bg-accent" aria-hidden />
      {count} open
    </span>
  );
}

function labelForTemplate(key: TemplateKey): string {
  return TEMPLATES.find((t) => t.key === key)?.label ?? key;
}

// ---------- New script modal ----------

function NewScriptModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [clientName, setClientName] = useState('');
  const [dashboardSlug, setDashboardSlug] = useState('');
  const [templateKey, setTemplateKey] = useState<TemplateKey>('explainer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await adminApi.createScript({
        title,
        client_name: clientName,
        template_key: templateKey,
        ...(dashboardSlug.trim() ? { dashboard_slug: dashboardSlug.trim() } : {}),
      });
      navigate(`/admin/scripts/${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create script.');
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center px-6 z-50"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="
          w-full min-w-0 max-w-md max-h-[calc(100dvh-32px)] overflow-y-auto bg-card border border-line rounded-md
          p-8 md:p-10 shadow-flare-lg/40
          animate-scale-in
        "
      >
        <Eyebrow accent>// New script</Eyebrow>
        <h2 className="font-display font-bold text-[28px] leading-[1.1] tracking-[-0.02em] mt-3 mb-2">
          Set up a draft
        </h2>
        <p className="text-muted text-[14px] mb-7">
          Fill in the basics. You can refine the script inside.
        </p>

        <FieldInput
          label="Title"
          value={title}
          onChange={setTitle}
          placeholder="e.g. Hero brand film"
          autoFocus
          disabled={busy}
        />

        <FieldInput
          label="Client"
          value={clientName}
          onChange={setClientName}
          placeholder="Client name"
          disabled={busy}
          className="mt-4"
        />

        <FieldInput
          label="Dashboard slug (optional)"
          value={dashboardSlug}
          onChange={(v) => setDashboardSlug(normaliseDashboardSlugInput(v))}
          placeholder="e.g. gigi-instyle"
          disabled={busy}
          required={false}
          pattern={DASHBOARD_SLUG_HTML_PATTERN}
          title={DASHBOARD_SLUG_HINT}
          className="mt-4"
        />

        <div className="mt-4">
          <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
            Template
          </span>
          <select
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value as TemplateKey)}
            className="
              w-full px-4 py-3 bg-elevated border border-line rounded-sm
              text-fg text-[15px]
              focus:border-accent focus:shadow-focus focus:outline-none
              transition-all duration-150
            "
          >
            {TEMPLATES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
          {templateKey === 'interview_brief' && (
            <p className="text-faint text-[12px] mt-2 leading-snug">
              For interview-led videos. Fields you can switch on, off and add to; only Client fields go to the client.
            </p>
          )}
        </div>

        {error && <p className="text-[13px] text-accent mt-4">{error}</p>}

        <div className="flex justify-end gap-3 mt-8">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            glow
            disabled={busy || !title || !clientName}
          >
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface FieldInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Defaults to true; pass false for optional fields. */
  required?: boolean;
  /** Native `pattern` check, applied only when the value is non-blank. */
  pattern?: string;
  /** Shown in the browser's validation message on a pattern mismatch. */
  title?: string;
  className?: string;
}

function FieldInput({
  label,
  value,
  onChange,
  placeholder,
  autoFocus,
  disabled,
  required = true,
  pattern,
  title,
  className = '',
}: FieldInputProps) {
  return (
    <label className={`block ${className}`}>
      <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        required={required}
        pattern={pattern}
        title={title}
        className="
          w-full px-4 py-3 bg-elevated border border-line rounded-sm
          text-fg text-[15px] placeholder:text-faint
          focus:border-accent focus:shadow-focus focus:outline-none
          transition-all duration-150
          disabled:opacity-50
        "
      />
    </label>
  );
}

// ---------- Delete confirm modal ----------

function DeleteConfirmModal({
  script,
  permanent,
  onCancel,
  onConfirm,
}: {
  script: ScriptActionTarget;
  /** True when the script is already in Trash and this is the irreversible
   *  drop. False for the first-step soft delete (script lands in Trash). */
  permanent: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);

  async function handleConfirm() {
    setBusy(true);
    try {
      await onConfirm();
    } catch {
      setBusy(false);
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md flex items-center justify-center px-6 z-50 animate-fade-in"
      onClick={busy ? undefined : onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card border border-line rounded-md p-8 shadow-flare-lg/30 animate-scale-in"
      >
        <Eyebrow accent>// {permanent ? 'Delete permanently' : 'Move to Trash'}</Eyebrow>
        <h2 className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-3 mb-2">
          {permanent ? 'Delete this script forever?' : 'Move this script to Trash?'}
        </h2>
        <p className="text-muted text-[14px] leading-[1.55] mb-2">
          <span className="text-fg font-medium">{script.title}</span>
          <span className="text-faint"> · </span>
          {script.client_name}
        </p>
        <p className="text-faint text-[13px] leading-[1.55] mb-7">
          {permanent
            ? "This permanently removes the script, its review URL, and any client comments. This action can't be undone."
            : 'You can restore it from the Trash tab any time in the next 30 days.'}
        </p>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleConfirm} disabled={busy}>
            {busy
              ? permanent
                ? 'Deleting…'
                : 'Moving…'
              : permanent
                ? 'Delete forever'
                : 'Move to Trash'}
          </Button>
        </div>
      </div>
    </div>
  );
}
